import React from 'react';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import {
    Box, Button, CircularProgress, Divider, FormHelperText,
    InputAdornment, InputLabel, MenuItem, OutlinedInput,
    Select, ToggleButton, ToggleButtonGroup, Typography
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import InputMask from 'react-input-mask';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import PageHeader from '../../../components/page-header';
import { useSupplierCreateEditController } from './context/supplier-create-edit-controller';
import { useSupplierCreateEditState } from './context/supplier-create-edit-context';

export function SupplierCreateEditComponent() {
    const {
        handleOnChange, handlePersonTypeChange,
        handleAddressChange, handleCountryChange, handleStateChange, handleZipCodeBlur,
        handleContactChange, handlePhysicalPersonChange, handleLegalPersonChange,
        handleSave, handleCancel,
        isSaving, isLoadingZipCode, countries, states, cities
    } = useSupplierCreateEditController();

    const { supplierRequest, showValidationErrors, supplierId } = useSupplierCreateEditState();
    const isEdit = !!supplierId;
    const personType = supplierRequest.supplierPersonType || 'PF';
    const isPF = personType === 'PF';

    return (
        <React.Fragment>
            <Grid item xs={12}>
                <PageHeader
                    title={isEdit ? 'Editar Fornecedor' : 'Novo Fornecedor'}
                    subtitle={isEdit ? 'Atualize as informações do fornecedor' : 'Cadastre um novo fornecedor no sistema'}
                    breadcrumbs={[{ label: 'Fornecedores', href: '/fornecedores' }, { label: isEdit ? 'Editar' : 'Novo' }]}
                />
            </Grid>

            <Grid item xs={12}>
                <Paper sx={{ p: 3, display: 'flex', flexDirection: 'column' }} elevation={3}>
                    <Grid container spacing={3}>

                        {/* Identificação */}
                        <Grid item xs={12}>
                            <Typography variant="h6" sx={{ mb: 2, color: 'primary.main', fontWeight: 600 }}>
                                Identificação
                            </Typography>
                            <Divider sx={{ mb: 2 }} />
                        </Grid>

                        <Grid item xs={12} md={8}>
                            <InputLabel required>Nome / Razão Social</InputLabel>
                            <OutlinedInput
                                fullWidth
                                autoComplete="off"
                                name="name"
                                value={supplierRequest.name || ''}
                                onChange={handleOnChange}
                                error={showValidationErrors && !supplierRequest.name}
                            />
                            {showValidationErrors && !supplierRequest.name && (
                                <FormHelperText error>Nome é obrigatório</FormHelperText>
                            )}
                        </Grid>

                        <Grid item xs={12} md={4} sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}>
                            <InputLabel>Tipo de Pessoa</InputLabel>
                            <ToggleButtonGroup
                                value={personType}
                                exclusive
                                onChange={(_, val) => val && handlePersonTypeChange(val)}
                                disabled={isEdit}
                                size="medium"
                                sx={{ mt: 0.5, width: '100%' }}
                            >
                                <ToggleButton value="PF" sx={{ flex: 1 }}>Pessoa Física</ToggleButton>
                                <ToggleButton value="PJ" sx={{ flex: 1 }}>Pessoa Jurídica</ToggleButton>
                            </ToggleButtonGroup>
                        </Grid>

                        {/* Pessoa Física */}
                        {isPF && (
                            <>
                                <Grid item xs={12}>
                                    <Typography variant="h6" sx={{ mb: 2, mt: 1, color: 'primary.main', fontWeight: 600 }}>
                                        Dados Pessoais
                                    </Typography>
                                    <Divider sx={{ mb: 2 }} />
                                </Grid>

                                <Grid item xs={12} md={3}>
                                    <InputLabel required>CPF</InputLabel>
                                    <InputMask
                                        mask="999.999.999-99"
                                        name="cpf"
                                        value={supplierRequest.physicalPerson?.cpf || ''}
                                        onChange={handlePhysicalPersonChange}
                                    >
                                        <OutlinedInput
                                            fullWidth
                                            name="cpf"
                                            autoComplete="off"
                                            error={showValidationErrors && !supplierRequest.physicalPerson?.cpf}
                                        />
                                    </InputMask>
                                    {showValidationErrors && !supplierRequest.physicalPerson?.cpf && (
                                        <FormHelperText error>CPF é obrigatório</FormHelperText>
                                    )}
                                </Grid>

                                <Grid item xs={12} md={3}>
                                    <InputLabel>RG</InputLabel>
                                    <OutlinedInput
                                        fullWidth
                                        autoComplete="off"
                                        name="rg"
                                        value={supplierRequest.physicalPerson?.rg || ''}
                                        onChange={handlePhysicalPersonChange}
                                    />
                                </Grid>
                            </>
                        )}

                        {/* Pessoa Jurídica */}
                        {!isPF && (
                            <>
                                <Grid item xs={12}>
                                    <Typography variant="h6" sx={{ mb: 2, mt: 1, color: 'primary.main', fontWeight: 600 }}>
                                        Dados Empresariais
                                    </Typography>
                                    <Divider sx={{ mb: 2 }} />
                                </Grid>

                                <Grid item xs={12} md={3}>
                                    <InputLabel required>CNPJ</InputLabel>
                                    <InputMask
                                        mask="99.999.999/9999-99"
                                        name="cnpj"
                                        value={supplierRequest.legalPerson?.cnpj || ''}
                                        onChange={handleLegalPersonChange}
                                    >
                                        <OutlinedInput
                                            fullWidth
                                            name="cnpj"
                                            autoComplete="off"
                                            error={showValidationErrors && !supplierRequest.legalPerson?.cnpj}
                                        />
                                    </InputMask>
                                    {showValidationErrors && !supplierRequest.legalPerson?.cnpj && (
                                        <FormHelperText error>CNPJ é obrigatório</FormHelperText>
                                    )}
                                </Grid>

                                <Grid item xs={12} md={5}>
                                    <InputLabel>Nome Fantasia</InputLabel>
                                    <OutlinedInput
                                        fullWidth
                                        autoComplete="off"
                                        name="tradeName"
                                        value={supplierRequest.legalPerson?.tradeName || ''}
                                        onChange={handleLegalPersonChange}
                                    />
                                </Grid>

                                <Grid item xs={12} md={4}>
                                    <InputLabel>Inscrição Estadual</InputLabel>
                                    <OutlinedInput
                                        fullWidth
                                        autoComplete="off"
                                        name="stateRegistration"
                                        value={supplierRequest.legalPerson?.stateRegistration || ''}
                                        onChange={handleLegalPersonChange}
                                    />
                                </Grid>

                                <Grid item xs={12} md={4}>
                                    <InputLabel>Inscrição Municipal</InputLabel>
                                    <OutlinedInput
                                        fullWidth
                                        autoComplete="off"
                                        name="municipalRegistration"
                                        value={supplierRequest.legalPerson?.municipalRegistration || ''}
                                        onChange={handleLegalPersonChange}
                                    />
                                </Grid>
                            </>
                        )}

                        {/* Endereço */}
                        <Grid item xs={12}>
                            <Typography variant="h6" sx={{ mb: 2, mt: 1, color: 'primary.main', fontWeight: 600 }}>
                                Endereço
                            </Typography>
                            <Divider sx={{ mb: 2 }} />
                        </Grid>

                        <Grid item xs={12} md={2}>
                            <InputLabel required>CEP</InputLabel>
                            <InputMask
                                mask="99999-999"
                                name="zipCode"
                                value={supplierRequest.address?.zipCode || ''}
                                onChange={handleAddressChange}
                                onBlur={handleZipCodeBlur}
                            >
                                <OutlinedInput
                                    fullWidth
                                    name="zipCode"
                                    autoComplete="off"
                                    error={showValidationErrors && !supplierRequest.address?.zipCode}
                                    endAdornment={
                                        isLoadingZipCode
                                            ? <InputAdornment position="end"><CircularProgress size={16} /></InputAdornment>
                                            : null
                                    }
                                />
                            </InputMask>
                            {showValidationErrors && !supplierRequest.address?.zipCode && (
                                <FormHelperText error>CEP é obrigatório</FormHelperText>
                            )}
                        </Grid>

                        <Grid item xs={12} md={4}>
                            <InputLabel required>País</InputLabel>
                            <Select
                                fullWidth
                                value={supplierRequest.address?.countryId ?? ''}
                                onChange={handleCountryChange}
                                disabled={isLoadingZipCode}
                                error={showValidationErrors && !supplierRequest.address?.countryId}
                            >
                                <MenuItem value=""><em>Selecionar</em></MenuItem>
                                {countries.map((c) => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
                            </Select>
                            {showValidationErrors && !supplierRequest.address?.countryId && (
                                <FormHelperText error>País é obrigatório</FormHelperText>
                            )}
                        </Grid>

                        <Grid item xs={12} md={3}>
                            <InputLabel required>Estado</InputLabel>
                            <Select
                                fullWidth
                                value={supplierRequest.address?.stateId ?? ''}
                                onChange={handleStateChange}
                                disabled={isLoadingZipCode || !supplierRequest.address?.countryId}
                                error={showValidationErrors && !supplierRequest.address?.stateId}
                            >
                                <MenuItem value=""><em>Selecionar</em></MenuItem>
                                {states.map((s) => <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>)}
                            </Select>
                            {showValidationErrors && !supplierRequest.address?.stateId && (
                                <FormHelperText error>Estado é obrigatório</FormHelperText>
                            )}
                        </Grid>

                        <Grid item xs={12} md={3}>
                            <InputLabel required>Município</InputLabel>
                            <Select
                                fullWidth
                                name="municipalityId"
                                value={supplierRequest.address?.municipalityId ?? ''}
                                onChange={handleAddressChange}
                                disabled={isLoadingZipCode || !supplierRequest.address?.stateId}
                                error={showValidationErrors && !supplierRequest.address?.municipalityId}
                            >
                                <MenuItem value=""><em>Selecionar</em></MenuItem>
                                {cities.map((c) => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
                            </Select>
                            {showValidationErrors && !supplierRequest.address?.municipalityId && (
                                <FormHelperText error>Município é obrigatório</FormHelperText>
                            )}
                        </Grid>

                        <Grid item xs={12} md={5}>
                            <InputLabel required>Logradouro</InputLabel>
                            <OutlinedInput
                                fullWidth
                                autoComplete="off"
                                name="street"
                                value={supplierRequest.address?.street || ''}
                                onChange={handleAddressChange}
                                disabled={isLoadingZipCode}
                                error={showValidationErrors && !supplierRequest.address?.street}
                            />
                            {showValidationErrors && !supplierRequest.address?.street && (
                                <FormHelperText error>Logradouro é obrigatório</FormHelperText>
                            )}
                        </Grid>

                        <Grid item xs={12} md={2}>
                            <InputLabel required>Número</InputLabel>
                            <OutlinedInput
                                fullWidth
                                autoComplete="off"
                                name="number"
                                value={supplierRequest.address?.number || ''}
                                onChange={handleAddressChange}
                                error={showValidationErrors && !supplierRequest.address?.number}
                            />
                            {showValidationErrors && !supplierRequest.address?.number && (
                                <FormHelperText error>Número é obrigatório</FormHelperText>
                            )}
                        </Grid>

                        <Grid item xs={12} md={2}>
                            <InputLabel>Complemento</InputLabel>
                            <OutlinedInput
                                fullWidth
                                autoComplete="off"
                                name="complement"
                                value={supplierRequest.address?.complement || ''}
                                onChange={handleAddressChange}
                                disabled={isLoadingZipCode}
                            />
                        </Grid>

                        <Grid item xs={12} md={3}>
                            <InputLabel required>Bairro</InputLabel>
                            <OutlinedInput
                                fullWidth
                                autoComplete="off"
                                name="neighborhood"
                                value={supplierRequest.address?.neighborhood || ''}
                                onChange={handleAddressChange}
                                disabled={isLoadingZipCode}
                                error={showValidationErrors && !supplierRequest.address?.neighborhood}
                            />
                            {showValidationErrors && !supplierRequest.address?.neighborhood && (
                                <FormHelperText error>Bairro é obrigatório</FormHelperText>
                            )}
                        </Grid>

                        {/* Contato */}
                        <Grid item xs={12}>
                            <Typography variant="h6" sx={{ mb: 2, mt: 1, color: 'primary.main', fontWeight: 600 }}>
                                Contato
                            </Typography>
                            <Divider sx={{ mb: 2 }} />
                        </Grid>

                        <Grid item xs={12} md={4}>
                            <InputLabel>E-mail</InputLabel>
                            <OutlinedInput
                                fullWidth
                                autoComplete="off"
                                name="email"
                                type="email"
                                value={supplierRequest.contact?.email || ''}
                                onChange={handleContactChange}
                            />
                        </Grid>

                        <Grid item xs={12} md={3}>
                            <InputLabel>Telefone</InputLabel>
                            <InputMask
                                mask="(99) 9999-9999"
                                name="phone"
                                value={supplierRequest.contact?.phone || ''}
                                onChange={handleContactChange}
                            >
                                <OutlinedInput fullWidth name="phone" autoComplete="off" />
                            </InputMask>
                        </Grid>

                        <Grid item xs={12} md={3}>
                            <InputLabel>Celular</InputLabel>
                            <InputMask
                                mask="(99) 99999-9999"
                                name="mobile"
                                value={supplierRequest.contact?.mobile || ''}
                                onChange={handleContactChange}
                            >
                                <OutlinedInput fullWidth name="mobile" autoComplete="off" />
                            </InputMask>
                        </Grid>

                        <Grid item xs={12} md={2}>
                            <InputLabel>Tel. Comercial</InputLabel>
                            <InputMask
                                mask="(99) 9999-9999"
                                name="commercialPhone"
                                value={supplierRequest.contact?.commercialPhone || ''}
                                onChange={handleContactChange}
                            >
                                <OutlinedInput fullWidth name="commercialPhone" autoComplete="off" />
                            </InputMask>
                        </Grid>

                        {/* Ações */}
                        <Grid item xs={12}>
                            <Box sx={{ display: 'flex', flexDirection: 'row', pt: 2, mt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                                <Box sx={{ flex: '1 1 auto' }} />
                                <Button
                                    variant="outlined"
                                    startIcon={<CancelIcon />}
                                    onClick={handleCancel}
                                    sx={{ mr: 1 }}
                                >
                                    Cancelar
                                </Button>
                                <LoadingButton
                                    variant="contained"
                                    startIcon={<SaveIcon />}
                                    onClick={handleSave}
                                    loading={isSaving}
                                    disabled={isSaving}
                                >
                                    Salvar
                                </LoadingButton>
                            </Box>
                        </Grid>

                    </Grid>
                </Paper>
            </Grid>
        </React.Fragment>
    );
}
